import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
  Bell,
  UserRound,
  ShieldCheck,
  Lock,
  Receipt,
  ClipboardList,
  ChevronRight,
  LogOut,
  User as UserIcon,
  Crown,
  Users,
  TrendingUp,
  Gift,
  Award,
  Download,
  Smartphone,
  ShieldPlus,
  Sparkles,
  Handshake,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { copyToClipboard } from "../lib/clipboard";
import { SiteHeader } from "../components/site/SiteHeader";
import { MobileBottomBar } from "../components/site/MobileBottomBar";

export default function Dashboard() {
  const { user, logout, refreshBalanceWithCashout } = useAuth();
  const navigate = useNavigate();

  const [balanceVisible, setBalanceVisible] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Recovery-bonus banner state — only shown to quick-signup users who
  // haven't linked a phone/email yet.
  const [recoveryStatus, setRecoveryStatus] = useState(null); // { eligible, bonus }
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/recovery-bonus/status");
        if (!cancelled) setRecoveryStatus(data);
      } catch {
        if (!cancelled) setRecoveryStatus({ eligible: false });
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const balance = user?.balance ?? 0;
  const vipPoints = user?.vip_points ?? 0;
  const currency = user?.currency || "BDT";
  const currencySymbol = currency === "BDT" ? "৳" : "$";
  const signupDate = (user?.created_at || "").slice(0, 10) || "—";

  const copyUsername = async () => {
    const ok = await copyToClipboard(user?.username || "");
    if (ok) toast.success("Username copied");
    else toast.error("Could not copy");
  };

  const onRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      // Pulls any leftover WICKET (transfer-wallet) balance back to the site
      // wallet before refreshing — so a manual click on Refresh always
      // reconciles money sitting in WICKET portal.
      await refreshBalanceWithCashout();
    } catch {
      toast.error("Could not refresh");
    } finally {
      setTimeout(() => setRefreshing(false), 500);
    }
  };

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const mask = (v) => (balanceVisible ? v : "**********");

  const rows = [
    { id: "notifications", icon: Bell, label: "Notifications", onClick: () => navigate("/profile/notifications") },
    { id: "personal-info", icon: UserRound, label: "Personal info", onClick: () => navigate("/profile/personal-info") },
    { id: "login-security", icon: Lock, label: "Password change", onClick: () => navigate("/profile/login-security") },
    { id: "two-factor", icon: ShieldCheck, label: "Two-factor authentication", onClick: () => navigate("/profile/2fa") },
    { id: "verification", icon: ShieldCheck, label: "Verification", onClick: () => navigate("/profile/verification") },
    { id: "transactions", icon: Receipt, label: "Transaction records", onClick: () => navigate("/profile/transactions") },
    { id: "bets", icon: ClipboardList, label: "Betting records", onClick: () => navigate("/profile/bets") },
  ];

  const extraRows = [
    { id: "free-spin", icon: Gift, label: "Daily Free Spin", onClick: () => navigate("/profile/free-spin") },
    { id: "turnover", icon: TrendingUp, label: "Turnover", onClick: () => navigate("/profile/turnover") },
    { id: "my-vip", icon: Crown, label: "My VIP", onClick: () => navigate("/profile/vip") },
    { id: "my-referral", icon: Users, label: "My referral", onClick: () => navigate("/profile/referral") },
    { id: "affiliate", icon: Award, label: "Affiliate Program", onClick: () => navigate("/profile/affiliate") },
    { id: "become-agent", icon: Handshake, label: "Become an Agent", onClick: () => navigate("/become-agent"), accent: "emerald" },
    { id: "download-app", icon: Smartphone, label: "Download App (APK)", onClick: () => navigate("/profile/download"), accent: "emerald" },
  ];

  return (
    <div className="min-h-screen bg-[#0a1119] text-white lg:pb-8">
      <div className="hidden lg:block">
        <SiteHeader />
      </div>

      {/* Mobile top bar — matches reference */}
      <div className="lg:hidden sticky top-0 z-30 bg-[#0a1119] border-b border-white/5">
        <div className="flex items-center justify-between px-4 h-14">
          <h1 className="text-2xl font-extrabold tracking-tight" data-testid="profile-title">Profile</h1>
          <button
            type="button"
            data-testid="profile-close"
            onClick={() => navigate("/")}
            aria-label="close"
            className="h-9 w-9 rounded-full flex items-center justify-center text-white/80 hover:text-white hover:bg-white/5"
          >
            <X size={22} />
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-4 pb-24 lg:pb-6 space-y-4" data-testid="dashboard-main">
        {/* User info row */}
        <div className="flex items-start gap-4 pt-2">
          <div
            aria-hidden
            className="h-16 w-16 shrink-0 rounded-full flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg,#2a3141 0%,#1a2030 100%)",
              boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.08)",
            }}
          >
            <UserIcon size={30} className="text-white/85" strokeWidth={1.6} />
          </div>
          <div className="min-w-0 flex-1 pt-1">
            <div className="text-white/50 text-sm">Username</div>
            <div className="flex items-center gap-2 mt-0.5">
              <span
                data-testid="profile-username"
                className="text-[22px] sm:text-2xl font-bold tracking-tight truncate"
              >
                {user?.username || "—"}
              </span>
              <button
                type="button"
                onClick={copyUsername}
                aria-label="copy username"
                data-testid="profile-copy-username"
                className="p-1 text-white/50 hover:text-white/90 rounded"
              >
                <Copy size={16} />
              </button>
            </div>
            {user?.player_id && (
              <div className="flex items-center gap-1.5 mt-1.5" data-testid="profile-player-id-row">
                <span className="text-[11px] uppercase tracking-wider text-white/40 font-semibold">
                  Player ID
                </span>
                <span
                  data-testid="profile-player-id"
                  className="text-white/85 text-[13px] font-mono tabular tracking-wide"
                >
                  {user.player_id}
                </span>
                <button
                  type="button"
                  onClick={async () => {
                    const ok = await copyToClipboard(String(user.player_id));
                    if (ok) toast.success("Player ID copied");
                  }}
                  aria-label="copy player id"
                  data-testid="profile-copy-player-id"
                  className="p-1 text-white/40 hover:text-white/85 rounded"
                >
                  <Copy size={13} />
                </button>
              </div>
            )}
            <div className="text-white/45 text-xs sm:text-sm mt-2">
              Sign up date : {signupDate}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            data-testid="profile-withdraw-btn"
            onClick={() => navigate("/profile/withdraw")}
            className="h-12 rounded-lg border border-white/15 bg-white/[0.06] hover:bg-white/[0.10] text-white font-semibold active:scale-[0.98] transition-all"
            style={{
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08)",
            }}
          >
            Withdrawal
          </button>
          <button
            type="button"
            data-testid="profile-deposit-btn"
            onClick={() => navigate("/profile/deposit")}
            className="h-12 rounded-lg border border-emerald-400/40 text-white font-bold active:scale-[0.98] transition-all"
            style={{
              background: "linear-gradient(180deg,#21c46e 0%,#19a55c 55%,#12904e 100%)",
              boxShadow: "0 4px 14px rgba(25,165,92,0.45), inset 0 1px 0 rgba(255,255,255,0.25)",
            }}
          >
            Deposit
          </button>
        </div>

        {/* Recovery bonus banner — quick-signup users only */}
        {recoveryStatus?.eligible && (
          <div
            data-testid="recovery-bonus-banner"
            className="relative overflow-hidden rounded-2xl border border-amber-400/30 shadow-[0_10px_30px_-12px_rgba(255,201,51,0.45)] group"
            style={{
              background:
                "linear-gradient(120deg, rgba(255,201,51,0.14) 0%, rgba(245,83,61,0.10) 55%, rgba(20,15,25,0.35) 100%)",
            }}
          >
            {/* Ambient decorative glow */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute -top-12 -right-12 h-40 w-40 rounded-full blur-3xl opacity-50"
              style={{ background: "radial-gradient(circle,#ffc933 0%,transparent 70%)" }}
            />
            <div className="relative p-4 sm:p-5 flex items-start gap-3.5">
              {/* Icon block */}
              <div
                aria-hidden="true"
                className="shrink-0 h-11 w-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-amber-300 to-orange-500 text-black shadow-[0_8px_20px_-4px_rgba(255,154,60,0.75)]"
              >
                <ShieldPlus size={20} strokeWidth={2.5} />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-200 text-[10px] font-black tracking-widest uppercase">
                    <Sparkles size={10} /> Limited offer
                  </span>
                </div>
                <div className="text-white font-black text-[15.5px] sm:text-base leading-tight">
                  Secure your account &amp; get{" "}
                  <span className="text-amber-300">
                    ৳{Number(recoveryStatus.bonus || 0).toFixed(0)} bonus
                  </span>
                </div>
                <div className="text-white/65 text-[12.5px] leading-snug mt-1">
                  Link a phone number or email so you can log back in if you ever lose your credentials. We&apos;ll credit the bonus instantly on first verification.
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    data-testid="recovery-bonus-add-phone"
                    onClick={() => navigate("/profile/phone-verify")}
                    className="h-9 px-3.5 rounded-lg inline-flex items-center gap-1.5 bg-white text-[#0a1119] font-black text-[12.5px] hover:bg-amber-100 active:scale-95 transition"
                  >
                    Add phone
                    <ChevronRight size={14} />
                  </button>
                  <button
                    type="button"
                    data-testid="recovery-bonus-add-email"
                    onClick={() => navigate("/profile/email-verify")}
                    className="h-9 px-3.5 rounded-lg inline-flex items-center gap-1.5 bg-white/10 border border-white/15 text-white font-bold text-[12.5px] hover:bg-white/15 active:scale-95 transition"
                  >
                    Add email
                    <ChevronRight size={14} />
                  </button>
                  <span className="text-white/40 text-[11px] font-medium">
                    Takes ~30 seconds
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Wallet card */}
        <div
          data-testid="wallet-card"
          className="rounded-xl p-4 sm:p-5 relative overflow-hidden"
          style={{
            background:
              "linear-gradient(135deg, rgba(25,165,92,0.18) 0%, rgba(25,165,92,0.06) 45%, rgba(10,17,25,0.2) 100%)",
            border: "1px solid rgba(25,165,92,0.25)",
          }}
        >
          <div
            aria-hidden
            className="absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-30"
            style={{ background: "radial-gradient(circle,#19a55c 0%,transparent 70%)" }}
          />

          <div className="flex items-center justify-between relative">
            <span className="text-white/75 text-sm sm:text-base">Main wallet</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setBalanceVisible((v) => !v)}
                aria-label="toggle balance visibility"
                data-testid="wallet-eye"
                className="h-8 w-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/5"
              >
                {balanceVisible ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
              <button
                type="button"
                onClick={onRefresh}
                aria-label="refresh balance"
                data-testid="wallet-refresh"
                className="h-8 w-8 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/5"
              >
                <RefreshCw size={17} className={refreshing ? "animate-spin" : ""} />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3 mt-2 relative">
            <span
              aria-hidden
              className="h-7 w-7 rounded-full flex items-center justify-center text-[12px] font-extrabold text-white"
              style={{ background: "linear-gradient(135deg,#19a55c 0%,#0f7a40 100%)" }}
            >
              {currencySymbol}
            </span>
            <span data-testid="wallet-balance" className="text-white text-lg sm:text-xl font-bold tabular-nums tracking-wider">
              {mask(balance.toLocaleString())}
            </span>
          </div>

          <div className="mt-5 flex items-center justify-between relative">
            <span className="text-white/75 text-sm sm:text-base">VIP Points</span>
            <button
              type="button"
              data-testid="vip-badge"
              onClick={() => navigate("/profile/vip")}
              className="flex items-center gap-1.5 h-7 pl-2 pr-2.5 rounded-md text-white text-xs font-bold tracking-wide relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(180deg,#d8e2ec 0%,#8a97a8 45%,#4a5565 100%)",
                boxShadow:
                  "inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -1px 0 rgba(0,0,0,0.35), 0 2px 6px rgba(0,0,0,0.35)",
                color: "#1a2030",
              }}
            >
              <span
                aria-hidden
                className="inline-flex h-4 w-4 rounded-sm rotate-45 shrink-0"
                style={{
                  background:
                    "linear-gradient(135deg,#c8f5dd 0%,#19a55c 55%,#0a5a2f 100%)",
                  boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.45)",
                }}
              />
              <span className="drop-shadow-sm">Normal</span>
              <ChevronRight size={14} className="text-[#1a2030]/80" />
            </button>
          </div>
          <div className="flex items-center gap-3 mt-2 relative">
            <span
              aria-hidden
              className="h-7 w-7 rounded-full flex items-center justify-center text-[11px] font-extrabold text-white"
              style={{ background: "linear-gradient(135deg,#d4a012 0%,#8a6508 100%)" }}
            >
              VP
            </span>
            <span data-testid="vip-points" className="text-white text-lg sm:text-xl font-bold tabular-nums tracking-wider">
              {mask(vipPoints.toLocaleString())}
            </span>
          </div>
        </div>

        {/* Menu rows */}
        <div className="rounded-xl overflow-hidden bg-[#101824] border border-white/5">
          {rows.map((row, i) => {
            const Icon = row.icon;
            const isAmber = row.accent === "amber";
            return (
              <button
                key={row.id}
                type="button"
                data-testid={`profile-row-${row.id}`}
                onClick={row.onClick}
                className={`w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-white/[0.03] active:bg-white/[0.06] transition-colors ${
                  i !== rows.length - 1 ? "border-b border-white/5" : ""
                }`}
              >
                <Icon
                  size={20}
                  className={isAmber ? "text-amber-400 shrink-0" : "text-white/80 shrink-0"}
                  strokeWidth={1.75}
                />
                <span className={`flex-1 font-medium text-[15px] ${isAmber ? "text-amber-300" : "text-white"}`}>
                  {row.label}
                </span>
                {isAmber ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[10.5px] font-bold uppercase tracking-wider shrink-0">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75 animate-ping" />
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-amber-400" />
                    </span>
                    Live
                  </span>
                ) : (
                  <ChevronRight size={18} className="text-white/40 shrink-0" />
                )}
              </button>
            );
          })}
        </div>

        {/* Turnover / My VIP / My referral / Download App */}
        <div className="rounded-xl overflow-hidden bg-[#101824] border border-white/5">
          {extraRows.map((row, i) => {
            const Icon = row.icon;
            const isDownload = row.id === "download-app";
            return (
              <button
                key={row.id}
                type="button"
                data-testid={`profile-row-${row.id}`}
                onClick={row.onClick}
                className={`w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-white/[0.03] active:bg-white/[0.06] transition-colors ${
                  i !== extraRows.length - 1 ? "border-b border-white/5" : ""
                }`}
              >
                <Icon
                  size={20}
                  className={isDownload ? "text-emerald-400 shrink-0" : "text-white/80 shrink-0"}
                  strokeWidth={1.75}
                />
                <span className={`flex-1 font-medium text-[15px] ${isDownload ? "text-emerald-300" : "text-white"}`}>
                  {row.label}
                </span>
                {isDownload ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[10.5px] font-bold uppercase tracking-wider shrink-0">
                    <Download size={12} /> APK
                  </span>
                ) : (
                  <ChevronRight size={18} className="text-white/40 shrink-0" />
                )}
              </button>
            );
          })}
        </div>

        {/* Logout */}
        <button
          type="button"
          data-testid="logout-btn"
          onClick={onLogout}
          className="w-full h-12 rounded-lg flex items-center justify-center gap-2 text-red-400 hover:text-red-300 bg-red-500/5 hover:bg-red-500/10 border border-red-500/20 font-semibold text-sm active:scale-[0.99] transition-all"
        >
          <LogOut size={16} />
          Log out
        </button>
      </div>

      <div className="lg:hidden">
        <MobileBottomBar onMenuClick={() => navigate("/")} />
      </div>
    </div>
  );
}
